'use client';

import { useTransition } from 'react';
import { useRouter } from 'next/navigation';
import {
  NAVIGATION_CONTEXTS,
  NAVIGATION_CONTEXT_ORDER,
  type NavigationContext,
} from '@/src/lib/navigation';
import { setActiveContextAction } from '@/src/server/actions/set-active-context.action';

export interface ContextTabsProps {
  activeContext: NavigationContext;
  availableContexts: readonly NavigationContext[];
}

export function ContextTabs({ activeContext, availableContexts }: ContextTabsProps) {
  const [isPending, startTransition] = useTransition();
  const router = useRouter();

  const visibleContexts = NAVIGATION_CONTEXT_ORDER.filter((ctx) => availableContexts.includes(ctx));

  if (visibleContexts.length === 0) {
    return null;
  }

  function handleSelect(context: NavigationContext) {
    if (context === activeContext) return;
    startTransition(async () => {
      const fd = new FormData();
      fd.set('context', context);
      const result = await setActiveContextAction(fd);
      if (result.ok) router.push(NAVIGATION_CONTEXTS[context].href);
    });
  }

  return (
    <nav
      aria-label="Kontext wechseln"
      data-tour="context-tabs"
      className="flex items-center gap-1 overflow-x-auto rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface-muted)] p-1"
    >
      {visibleContexts.map((context) => {
        const meta = NAVIGATION_CONTEXTS[context];
        const isActive = context === activeContext;
        const accent = CONTEXT_ACCENTS[meta.accent];
        return (
          <button
            key={context}
            type="button"
            onClick={() => handleSelect(context)}
            aria-current={isActive ? 'page' : undefined}
            aria-pressed={isActive}
            disabled={isPending}
            className={[
              'group inline-flex shrink-0 items-center gap-2 rounded-[var(--k-radius-sm)] px-3 py-1.5 text-sm font-semibold transition duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--k-color-focus-ring)] disabled:cursor-wait disabled:opacity-60',
              isActive
                ? `${accent.activeBg} ${accent.activeText} shadow-[var(--k-shadow-xs)]`
                : 'text-[var(--k-color-text-secondary)] hover:bg-[var(--k-color-surface)] hover:text-[var(--k-color-text-primary)]',
            ].join(' ')}
            title={meta.description}
          >
            <span
              aria-hidden="true"
              className={[
                'flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold tracking-tight',
                isActive ? accent.badge : accent.iconMuted,
              ].join(' ')}
            >
              {meta.icon}
            </span>
            <span className="whitespace-nowrap">{meta.shortLabel}</span>
          </button>
        );
      })}
    </nav>
  );
}

const CONTEXT_ACCENTS: Record<
  string,
  { activeBg: string; activeText: string; badge: string; iconMuted: string }
> = {
  work: {
    activeBg: 'bg-[var(--k-color-area-work-soft)]',
    activeText: 'text-[var(--k-color-role-work-text)]',
    badge: 'bg-[var(--k-color-role-work-icon)] text-white',
    iconMuted:
      'bg-[var(--k-color-surface)] text-[var(--k-color-text-muted)] ring-1 ring-[var(--k-color-border-subtle)]',
  },
  operations: {
    activeBg: 'bg-[var(--k-color-area-work-soft)]',
    activeText: 'text-[var(--k-color-role-work-text)]',
    badge: 'bg-[var(--k-color-role-work-icon)] text-white',
    iconMuted:
      'bg-[var(--k-color-surface)] text-[var(--k-color-text-muted)] ring-1 ring-[var(--k-color-border-subtle)]',
  },
  accounting: {
    activeBg: 'bg-[var(--k-color-area-accounting-soft)]',
    activeText: 'text-[var(--k-color-role-accounting-text)]',
    badge: 'bg-[var(--k-color-role-accounting-icon)] text-white',
    iconMuted:
      'bg-[var(--k-color-surface)] text-[var(--k-color-text-muted)] ring-1 ring-[var(--k-color-border-subtle)]',
  },
  advisor: {
    activeBg: 'bg-[var(--k-color-area-advisor-soft)]',
    activeText: 'text-[var(--k-color-role-advisor-text)]',
    badge: 'bg-[var(--k-color-role-advisor-icon)] text-white',
    iconMuted:
      'bg-[var(--k-color-surface)] text-[var(--k-color-text-muted)] ring-1 ring-[var(--k-color-border-subtle)]',
  },
  tenant: {
    activeBg: 'bg-[var(--k-color-area-admin-soft)]',
    activeText: 'text-[var(--k-color-role-tenant-text)]',
    badge: 'bg-[var(--k-color-role-tenant-icon)] text-white',
    iconMuted:
      'bg-[var(--k-color-surface)] text-[var(--k-color-text-muted)] ring-1 ring-[var(--k-color-border-subtle)]',
  },
  system: {
    activeBg: 'bg-[var(--k-color-area-system-soft)]',
    activeText: 'text-[var(--k-color-role-system-text)]',
    badge: 'bg-[var(--k-color-role-system-icon)] text-white',
    iconMuted:
      'bg-[var(--k-color-surface)] text-[var(--k-color-text-muted)] ring-1 ring-[var(--k-color-border-subtle)]',
  },
};
